<script lang="ts" setup>
import { ChartOverlay } from '@forgd/ui-kit/charts/chart-overlay'
import { ChartTitle } from '@forgd/ui-kit/charts/chart-title'
import { getBarWidth } from '#core/services/unovis/helpers'
import { formatValue } from '@forgd/formatter'
import { ColorKey, ColorKeyLabel, ColorKeyValue } from '@forgd/ui-kit/color-key'
import { VisAxis, VisStackedBar, VisXYContainer } from '@unovis/vue'
import { useElementSize } from '@vueuse/core'

const props = defineProps<{
  data: { value: number, label: string, index: number }[]
  color: string
  height: number
  gap: number
  title: string
}>()

const chartContainer = ref<HTMLElement | null>(null)
const { width: containerWidth } = useElementSize(chartContainer)

const chartData = computed(() => props.data)

const barWidth = computed(() => {
  return getBarWidth(chartData.value, { width: containerWidth.value, gap: props.gap }, 30)
})

const labelsMap = computed(() => new Map(chartData.value.map(item => [item.index, item.label])))

function xTickFormat(_: number | Date, index: number) {
  return labelsMap.value.get(index) ?? ''
}

function yTickFormat(v: number | Date) {
  return formatValue(v, 'currency')
}

const xTicks = computed(() => chartData.value.map(item => item.index))

const yDomain = computed(() => {
  if (chartData.value.length === 0) {
    return [0, 1]
  }
  return [0, Math.max(...chartData.value.map(item => item.value)) * 1.05]
})
</script>

<template>
  <div ref="chartContainer" data-el="PrBarChart" class="flex size-full min-w-0 flex-col overflow-hidden">
    <ChartTitle :text="title" />
    <PrChartPlaceholder :height="height">
      <VisXYContainer :data="chartData" :y-domain="yDomain" :height="height" class="w-full">
        <VisStackedBar
          :x="(d: any) => d.index"
          :y="(d: any) => d.value"
          :color="[props.color]"
          orientation="vertical"
          :bar-width="barWidth"
        />
        <VisAxis
          type="x"
          :tick-format="xTickFormat"
          :tick-values="xTicks"
          :tick-line="false"
          :grid-line="false"
        />
        <VisAxis
          type="y"
          :tick-format="yTickFormat"
          :num-ticks="7"
          :tick-line="false"
          :grid-line="true"
        />
        <ChartOverlay>
          <template #default="{ d }">
            <ColorKey :color="props.color" layout="justify" class="min-w-50">
              <ColorKeyLabel>{{ String(d.label) }}</ColorKeyLabel>
              <ColorKeyValue>{{ formatValue(d.value, 'currency') }}</ColorKeyValue>
            </ColorKey>
          </template>
        </ChartOverlay>
      </VisXYContainer>
    </PrChartPlaceholder>
  </div>
</template>
